import { Injectable } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { AdminGrant } from './admin-grant.model';
import { FirebaseApiProvider } from 'libs/api/providers/firebase-api.provider';

const COLLECTION = 'adminGrants';

@Injectable({
  providedIn: 'root',
})
export class AdminGrantService {
  constructor(private apiService: FirebaseApiProvider) {}

  /**
   * Lecture ponctuelle (`fetchAll`), pas un listener — même correctif que
   * `AffiliationService.getAllAffiliations` : `collectionChanges()` ne tire
   * jamais d'événement quand le résultat est vide, ce qui bloquait
   * indéfiniment AuthAdminGuard/SuperAdminGuard pour toute identité sans
   * droit admin (l'immense majorité des comptes, seuls 27 étant migrés).
   */
  getActiveGrants(identityId: string): Observable<AdminGrant[]> {
    return from(
      this.apiService.fetchAll<AdminGrant>(COLLECTION, AdminGrant.fromObject, [
        { fieldPath: 'identityId', condition: '==', value: identityId },
        { fieldPath: 'revokedAt', condition: '==', value: null },
      ]),
    );
  }

  isCenterAdmin(identityId: string, trainingCenterId: string): Observable<boolean> {
    return this.getActiveGrants(identityId).pipe(
      map((grants) => grants.some((g) => g.scope === 'center' && g.trainingCenterId === trainingCenterId)),
    );
  }

  isNetworkAdmin(identityId: string, networkId: string): Observable<boolean> {
    return this.getActiveGrants(identityId).pipe(
      map((grants) => grants.some((g) => g.scope === 'network' && g.networkId === networkId)),
    );
  }

  /** Admin de quoi que ce soit, sans égard au périmètre — la porte d'entrée de l'app admin. */
  hasAnyActiveGrant(identityId: string): Observable<boolean> {
    return this.getActiveGrants(identityId).pipe(map((grants) => grants.length > 0));
  }

  /** Admin réseau d'au moins un réseau, sans préciser lequel. */
  hasAnyNetworkGrant(identityId: string): Observable<boolean> {
    return this.getActiveGrants(identityId).pipe(map((grants) => grants.some((g) => g.scope === 'network')));
  }

  async grant(adminGrant: AdminGrant): Promise<string> {
    return this.apiService.create<AdminGrant>(COLLECTION, adminGrant, AdminGrant.toObject);
  }

  async revoke(grantId: string, revokedAt: string): Promise<void> {
    await this.apiService.update(`${COLLECTION}/${grantId}`, { revokedAt }, (data) => data);
  }
}
